<template>
    <StateSwitch :state="status">
        <template #default>
            <slot
                name="default"
                :event="event"
                :artist-ids="artistIds"
                :event-content-id="eventContentId"
            />
        </template>
    </StateSwitch>
</template>

<script setup lang="ts">
const props = defineProps<{
    eventId: number;
}>();

const { eventId } = toRefs(props);

const eventsStore = useEventsStore();
const userStore = useUserStore();

const fetchEventUrl = computed(() => `/v2.0/events/${eventId.value}`);

const { data, status, error } = await useAsyncFetchLaravel<{ data: EventDate }>(
    fetchEventUrl,
    {
        key: `event-${eventId.value}`,
        params: {
            use_cache: eventsStore.needsRefresh === 0
        },
        watch: [
            eventId,
            () => eventsStore.needsRefresh
        ]
    }
);

const seoDescription = computed(() => {
    if (data.value?.data && data.value?.data.description) {
        // regular expression to strip html tags for the seo description
        return data.value.data.description.replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, '');
    }

    return null;
});

const event = computed(() => data.value?.data);

const artistIds = computed(() => {
    if (event.value?.artists) {
        return event.value?.artists?.map(({ id }) => id);
    }

    return [];
});

const eventContentId = computed(() => event.value?.eventContentId);

watch(event, () => {
    if (import.meta.server) {
        return;
    }

    setTimeout(function() {
        document.querySelector('.event-modal')?.scroll({
            top: 0,
            behavior: 'smooth'
        });
    }, 300);

    if (event.value) {
        eventsStore.setActiveEventData(
            event.value,
            fetchEventUrl.value
        );
    }
}, {
    immediate: true
});

if (!data.value?.data) {
    throw createError({ statusCode: 404, statusMessage: JSON.stringify(error.value) });
} else {
    useHead({
        title: data.value.data.title,
        meta: [
            {
                key: 'description',
                name: 'description',
                content: seoDescription.value
            },
            {
                key: 'author',
                name: 'author',
                content: 'We Are Public'
            },
            {
                key: 'og:image',
                property: 'og:image',
                content: data.value.data.featuredImage?.url_box_lg
            },
            {
                key: 'og:title',
                property: 'og:title',
                content: data.value.data.title
            },
            {
                key: 'og:description',
                property: 'og:description',
                content: seoDescription.value
            },
            {
                key: 'twitter:image',
                name: 'twitter:image',
                content: data.value.data.featuredImage?.url_box_lg
            }
        ]
    });
}

const hasReservation = computed(() => userStore.hasReservation(eventId.value));

const paidPriceEndpoint = computed(() => `/v2.0/user/me/reservations/${eventId.value}`);

type PaidPriceData = {
    paidPrice: number;
    type: string;
}

const fetchPaidPrice = async() => {
    if (!hasReservation.value) {
        return;
    }

    const { data: paidPriceData } = await useAsyncFetchLaravel<PaidPriceData | null>(paidPriceEndpoint);

    if (paidPriceData?.value) {
        eventsStore.paidPrice = paidPriceData.value.paidPrice;
    }
};

watch(hasReservation, (updated) => {
    if (updated) {
        fetchPaidPrice();
    }
}, {
    immediate: true,
});
</script>
